import { FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import {
    usePathfinderAisData,
    usePathfinderData,
    usePathfinderFilesExists,
    usePFMetadataQuery,
} from '../../../requests/reports';
import { Spinner } from '../../../shared/styles/Spinner';
import PathfinderMap from '../../../shared/components/PathfinderMap';
import { useParams } from 'react-router-dom';
import { PathfinderList } from '../components/PathfinderList';
import { Feature } from 'geojson';
import { PathfinderLeftSidebar } from '../components/PathfinderLeftSidebar';
import { PathfinderErrorHandler } from '../../../utils/errorHandling';

import { isValidDateString } from '../../../utils/date';
import { PathfinderSlider } from '../../../utils/Slider';

const Wrapper = styled.div`
    position: fixed;
    width: 100%;
    height: 100%;
`;

export const AbsoluteSpinner = styled(Spinner)`
    position: absolute;
`;

export const ColorBall = styled.div<{ $backgroundColor?: string | null }>`
    height: 16px;
    width: 16px;
    border-radius: 17px;
    background-color: ${(props) => (props.$backgroundColor ? props.$backgroundColor : '#ecda80')};
    align-self: center;
    text-align: center;
    margin-right: 10px;
`;
export interface IChosenBoatObject {
    lon?: number;
    lat?: number;
    timestamp?: string;
    image_path?: number;
    id?: number | string;
}

export const PathfinderReport: FC = () => {
    const params = useParams();
    const [mmsi, setMmsi] = useState<string | null>(null);
    const { data: reportMetadata, isSuccess: metaDataIsSuccess } = usePFMetadataQuery(
        params.id ? params.id : null,
        mmsi
    );
    const { data: pathfinderData, isLoading } = usePathfinderData(params);
    const { data: pathfinderFilesExists } = usePathfinderFilesExists(params);
    const { data: pathfinderAisData } = usePathfinderAisData(params);

    const containerRef = useRef<HTMLDivElement>(null);
    const [fromDate, setFromDate] = useState<string>('');
    const [toDate, setToDate] = useState<string>('');
    const [chosenBoatObject, setChosenBoatObject] = useState<IChosenBoatObject | null>(null);
    const [hoveredPathElement, setHoveredPathElement] = useState<number | null>(null);
    const [boatMetaData, setBoatMetaData] = useState<Feature | null>(null);
    const [reportName, setReportName] = useState<string | null>(null);
    const [shipImo, setShipImo] = useState<string | null>(null);
    const [havePlanet, setHavePlanet] = useState<boolean>(false);
    const [hasOilPipelines, setHasOilPipelines] = useState<boolean>(false);
    const [filterStartDate, setFilterStartDate] = useState<number>(-1);
    const [filterEndDate, setFilterEndDate] = useState<number>(-1);
    const [sliderMinDate, setSliderMinDate] = useState<number>(-1);
    const [sliderMaxDate, setSliderMaxDate] = useState<number>(-1);

    if (metaDataIsSuccess && reportMetadata && reportName == null && shipImo == null) {
        if (reportMetadata.reportName) {
            setReportName(reportMetadata.reportName);
            document.title = reportMetadata.reportName;
        }
        if (reportMetadata.imo) {
            setShipImo(reportMetadata.imo);
        }
    }

    // Setting report params such as mmsi, timestamps and name (if exists)
    useEffect(() => {
        const incomingParameters = typeof params['id'] !== 'undefined' ? params['id'].split('_') : [];
        if (incomingParameters.length >= 3) {
            setMmsi(incomingParameters[0]);
            const from = incomingParameters[1].split('T')[0];
            const to = incomingParameters[2].split('T')[0];
            setFromDate(from);
            setToDate(to);
            if (document.title === 'Vake') {
                // Vake is the default title, only change to mmsi for default case.
                document.title = incomingParameters[0];
            }
            if (isValidDateString(from) && isValidDateString(to)) {
                //Adding buffer ensuring timeslider does not remove any elements at the sides
                const minDate = new Date(from).getTime() - 100000000;
                const maxDate = new Date(to).getTime() + 100000000;
                setSliderMinDate(minDate);
                setSliderMaxDate(maxDate);
                setFilterStartDate(minDate);
                setFilterEndDate(maxDate);
            }
        } else if (incomingParameters.length === 1) {
            if (typeof incomingParameters[0] === 'string') {
                const name = incomingParameters[0].charAt(0).toUpperCase() + incomingParameters[0].slice(1);
                setReportName(name);
            }
        }
    }, [params]);

    useEffect(() => {
        if (pathfinderData) {
            if (pathfinderData.ais_positions && pathfinderData.ais_positions.features) {
                pathfinderData.ais_positions.features.forEach((element) => {
                    if (!havePlanet && element.properties?.image_source === 'planet_scope') {
                        setHavePlanet(true);
                    }
                });
            }
            if (pathfinderData.oil_pipelines) {
                setHasOilPipelines(true);
            }
        }
    }, [pathfinderData, isLoading, havePlanet]);
    useEffect(() => window.scrollTo(0, 0), []);
    return (
        <Wrapper>
            {params ? (
                <>
                    <PathfinderLeftSidebar
                        reportName={reportName}
                        mmsi={mmsi}
                        shipImo={shipImo}
                        fromDate={fromDate}
                        toDate={toDate}
                        havePlanet={havePlanet}
                        hasOilPipelines={hasOilPipelines}
                    />
                    {pathfinderData && (pathfinderData.ais_positions || pathfinderData.ais_tracks) ? (
                        <>
                            {pathfinderData.ais_positions ? (
                                <PathfinderList
                                    aisPositions={pathfinderData?.ais_positions}
                                    chosenBoatObject={chosenBoatObject}
                                    boatMetaData={boatMetaData}
                                    setBoatMetaData={setBoatMetaData}
                                    setHoveredPathElement={setHoveredPathElement}
                                    mmsi={mmsi}
                                    reportName={reportName}
                                />
                            ) : null}
                            {filterStartDate !== -1 &&
                            filterEndDate !== -1 &&
                            sliderMinDate !== -1 &&
                            sliderMaxDate !== -1 ? (
                                <PathfinderSlider
                                    startDate={filterStartDate}
                                    endDate={filterEndDate}
                                    minDate={sliderMinDate}
                                    maxDate={sliderMaxDate}
                                    setFilterStartDate={setFilterStartDate}
                                    setFilterEndDate={setFilterEndDate}
                                />
                            ) : null}
                        </>
                    ) : null}
                    <PathfinderErrorHandler
                        hasAIS={pathfinderFilesExists?.has_ais}
                        hasImages={pathfinderFilesExists?.has_images}
                        createdAt={pathfinderFilesExists?.report_created_at}
                        isLoading={isLoading}
                    />
                </>
            ) : null}
            <PathfinderMap
                containerRef={containerRef}
                pathfinderData={pathfinderData}
                pathfinderAisData={pathfinderAisData}
                boatMetaData={boatMetaData}
                setChosenBoatObject={setChosenBoatObject}
                hoveredPathElement={hoveredPathElement}
                filterStartDate={filterStartDate}
                filterEndDate={filterEndDate}
            />
        </Wrapper>
    );
};
